import AutoAwesome from '@mui/icons-material/AutoAwesome'
import ContentCut from '@mui/icons-material/ContentCut'
import ZoomIn from '@mui/icons-material/ZoomIn'
import ZoomOut from '@mui/icons-material/ZoomOut'
import {
  Box,
  Button,
  Divider,
  IconButton,
  Slider,
  Stack,
  Typography,
} from '@mui/material'
import { QueryKey } from '@tanstack/react-query'
import useLocalStorage from '@/hooks/useLocalStorage'
import { useWorkflowStateEventStatuses } from '@/service-library/hooks/events'
import { Document } from '@/types/documents'
import ChildDocumentsCard from './ChildDocumentsCard'
import SplitDocumentOptions from './SplitDocumentOptions'
import SplitDocumentImages from './SplitDocumentPages'
import useSplitDocuments from './useSplitDocuments'

type SplitDocumentViewProps = {
  document: Document
  documentQueryKey?: QueryKey
}

const ZOOM_TILE_WIDTHS = [160, 240, 340, 440, 540]
const ZOOM_USE_FULL_IMAGE_THRESHOLD = 3 // zoom level 3+ uses full-resolution images

const emptyStateMessageByStatus: Record<string, string> = {
  waiting_for_upload: 'This document is waiting for upload.',
  page_generation_started: 'Pages are still being generated for this document.',
  processing_failed:
    "This document couldn't be processed, so no pages are available.",
}

export default function SplitDocumentView({
  document,
  documentQueryKey,
}: SplitDocumentViewProps) {
  const data = useSplitDocuments({
    document,
    documentQueryKey,
  })

  const { splitPoints, createDocuments, isPending } = data

  const RUNNING_STATUSES = ['waiting', 'running', 'running_async']
  const { eventStatuses } = useWorkflowStateEventStatuses({
    filters: {
      document_id: document.id,
    },
    refetchInterval: (query) => {
      const allResults =
        query.state.data?.pages?.flatMap((page) => page.results) ?? []
      const atLeastOneRunning = allResults.some((eventInstance) =>
        RUNNING_STATUSES.includes(eventInstance.status),
      )
      return atLeastOneRunning ? 2000 : false
    },
  })
  const isEventRunning = eventStatuses.some((eventInstance) =>
    RUNNING_STATUSES.includes(eventInstance.status),
  )

  const [zoomLevel, setZoomLevel] = useLocalStorage<number>(
    'split-view-zoom',
    2,
  )
  const tileWidth = ZOOM_TILE_WIDTHS[zoomLevel - 1] ?? ZOOM_TILE_WIDTHS[1]
  const useFullImage = zoomLevel >= ZOOM_USE_FULL_IMAGE_THRESHOLD
  const pages = document.document_pages ?? []
  const uploadStatus = document.upload_status
  const emptyStateMessage = emptyStateMessageByStatus[uploadStatus]

  const numDocuments = splitPoints.length + 1

  return (
    <Stack
      sx={{
        height: '100%',
        background: (theme) =>
          theme.palette.mode === 'dark'
            ? undefined
            : theme.palette.background.default,
      }}
    >
      {pages.length > 1 ? (
        <Stack sx={{ height: '100%' }}>
          {/* Toolbar */}
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{
              px: 2,
              py: 1,
              borderBottom: 1,
              borderColor: 'divider',
              backgroundColor: (theme) =>
                theme.palette.mode === 'dark'
                  ? theme.palette.background.paper
                  : theme.palette.background.default,
              flexShrink: 0,
              zIndex: 2,
              gap: 2,
              flexWrap: 'wrap',
            }}
          >
            {/* Left section: Split options + density */}
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{ flexShrink: 0 }}
            >
              <Box sx={{ pl: 0.75 }}>
                <SplitDocumentOptions
                  pages={document.document_pages}
                  splitDocumentsData={data}
                />
              </Box>
              <Divider orientation="vertical" flexItem />
              <Stack
                direction="row"
                spacing={0.5}
                alignItems="center"
                sx={{ minWidth: 120 }}
              >
                <IconButton
                  size="small"
                  disabled={zoomLevel <= 1}
                  onClick={() => setZoomLevel(Math.max(1, zoomLevel - 1))}
                >
                  <ZoomOut />
                </IconButton>
                <Slider
                  value={zoomLevel}
                  min={1}
                  max={5}
                  step={1}
                  onChange={(_, value) => setZoomLevel(value as number)}
                  size="small"
                  sx={{ mx: 1 }}
                />
                <IconButton
                  size="small"
                  disabled={zoomLevel >= 5}
                  onClick={() => setZoomLevel(Math.min(5, zoomLevel + 1))}
                >
                  <ZoomIn />
                </IconButton>
              </Stack>

              {splitPoints.length > 0 &&
                document.suggested_splits?.suggested_by_ai && (
                  <Stack direction="row" spacing={1} alignItems="center">
                    <AutoAwesome color="secondary" fontSize="small" />
                    <Typography variant="body2" color="secondary">
                      {splitPoints.length} split
                      {splitPoints.length === 1 ? '' : 's'} suggested by PixyAI
                    </Typography>
                  </Stack>
                )}
            </Stack>

            {/* Right section: Status + Delete original + AI + Split */}
            <Stack direction="row" spacing={1.5} alignItems="center">
              {splitPoints.length > 0 && (
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <ContentCut sx={{ fontSize: 16, color: 'text.secondary' }} />
                  <Typography variant="body2" color="text.secondary">
                    {splitPoints.length} split
                    {splitPoints.length === 1 ? '' : 's'} &rarr; {numDocuments}{' '}
                    documents
                  </Typography>
                </Stack>
              )}
              <Typography variant="caption" color="text.secondary">
                This document will not be deleted after splitting.
              </Typography>
              <Divider orientation="vertical" flexItem />
              <Button
                disabled={!splitPoints.length || isPending || isEventRunning}
                variant="contained"
                onClick={() => {
                  createDocuments()
                }}
              >
                Split
              </Button>
            </Stack>
          </Stack>

          {/* Hint bar - shown when no splits are set */}
          {splitPoints.length === 0 && (
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              justifyContent="center"
              sx={{
                py: 0.75,
                px: 2,
                flexShrink: 0,
              }}
            >
              <ContentCut sx={{ fontSize: 14, color: 'text.secondary' }} />
              <Typography variant="caption" color="text.secondary">
                Click between pages to set split points. Each section becomes a
                new document.
              </Typography>
            </Stack>
          )}

          {/* Page images area */}
          <Stack
            justifyContent="center"
            flexGrow={1}
            sx={{ position: 'relative', minHeight: 0 }}
          >
            <ChildDocumentsCard document={document} isSplitting={isPending} />
            <SplitDocumentImages
              document={document}
              splitDocumentsData={data}
              isSplitView
              tileWidth={tileWidth}
              useFullImage={useFullImage}
            />
          </Stack>
        </Stack>
      ) : pages.length === 1 ? (
        <Stack alignItems="center" spacing={3} sx={{ p: 2, margin: 'auto 0' }}>
          <Typography>This document only has one page.</Typography>
          <Box
            sx={{
              height: '70vh',
              textAlign: 'center',
            }}
          >
            <img
              loading="lazy"
              src={pages[0].image_urls.full}
              style={{ objectFit: 'contain', height: '100%', width: '100%' }}
            />
          </Box>
        </Stack>
      ) : (
        <Stack alignItems="center" spacing={2} sx={{ p: 2, margin: 'auto 0' }}>
          <Typography>{emptyStateMessage}</Typography>
          {uploadStatus !== 'processing_failed' && (
            <Typography variant="body2" color="text.secondary">
              Try again in a moment while processing completes.
            </Typography>
          )}
        </Stack>
      )}
    </Stack>
  )
}
